import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import PageTitle from '../../Component/Comman/PageTitle';
import { fetchForexTrades } from '../../Redux/Actions/dataActions';
import { fetchRates, getRateBySymbol, formatWithAllDecimals } from '../../services/ratesService';
import TradeDetailsModal from '../../Component/Comman/TradeDetailsModal';

function ForexPage({ forexTrades, fetchForexTrades, loading }) {
    const hasFetched = useRef(false);
    const ratesIntervalRef = useRef(null);
    const [liveRates, setLiveRates] = useState([]);
    const [selectedTrade, setSelectedTrade] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [filters, setFilters] = useState({
        status: 'active',
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    useEffect(() => {
        if (!hasFetched.current && !loading) {
            hasFetched.current = true;
            fetchForexTrades(filters);
        }
    }, [fetchForexTrades, loading]);

    useEffect(() => {
        if (hasFetched.current) {
            fetchForexTrades(filters);
        }
    }, [filters, fetchForexTrades]);

    // Fetch live rates
    const fetchLiveRates = useCallback(async () => {
        try {
            const rates = await fetchRates();
            setLiveRates(rates || []);
        } catch (error) {
            console.error('Error fetching live rates:', error);
        }
    }, []);

    // Set up interval to fetch rates every second
    useEffect(() => {
        // Fetch immediately
        fetchLiveRates();
        
        // Set up interval to fetch every second
        ratesIntervalRef.current = setInterval(() => {
            fetchLiveRates();
        }, 1000);

        // Cleanup interval on unmount
        return () => {
            if (ratesIntervalRef.current) {
                clearInterval(ratesIntervalRef.current);
            }
        };
    }, [fetchLiveRates]);

    // Calculate live profit/loss for active trades
    const calculateLivePL = useCallback((trade) => {
        // For completed trades, use stored profit/loss
        if (trade.status === 'completed' || trade.status === 'cancelled') {
            return trade.profit || trade.profitLoss || 0;
        }

        // For active trades, calculate using live rates
        if (trade.status === 'active' && trade.symbol) {
            const rateData = getRateBySymbol(liveRates, trade.symbol);
            if (rateData && rateData.rate) {
                const currentPrice = parseFloat(rateData.rate);
                const entryPrice = parseFloat(trade.entryPrice) || 0;
                const quantity = parseFloat(trade.quantity) || 0;
                
                if (currentPrice && entryPrice && quantity) {
                    // Calculate P/L based on direction
                    const priceDiff = trade.direction === 'buy' || trade.direction === 'long'
                        ? currentPrice - entryPrice
                        : entryPrice - currentPrice;
                    
                    return priceDiff * quantity;
                }
            }
        }

        // Fallback to stored values
        return trade.profit || trade.profitLoss || 0;
    }, [liveRates]);

    // Get current price from live rates (returns string to preserve decimals)
    const getCurrentPrice = useCallback((trade) => {
        if (trade.status === 'completed' || trade.status === 'cancelled') {
            return formatWithAllDecimals(trade.exitPrice || trade.currentPrice || 0);
        }

        if (trade.status === 'active' && trade.symbol) {
            const rateData = getRateBySymbol(liveRates, trade.symbol);
            if (rateData && rateData.rate) {
                // Return the original rate string to preserve all decimals
                return rateData.rate;
            }
        }

        return formatWithAllDecimals(trade.currentPrice || trade.exitPrice || 0);
    }, [liveRates]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: name === 'page' ? parseInt(value) : 1
        }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({
            ...prev,
            page: newPage
        }));
    };

    const handleStatusChange = (newStatus) => {
        setFilters(prev => ({
            ...prev,
            status: newStatus,
            page: 1 // Reset to first page when status changes
        }));
    };

    const trades = forexTrades?.data || [];
    const pagination = forexTrades?.pagination || {};
    const stats = forexTrades?.stats || {};

    return (
        <>
            <div className="body-header border-bottom d-flex py-3 mb-3">
                <PageTitle pagetitle='Forex Trades' sidebutton={false} />
            </div>
            <div className="container-xxl">
                {/* Statistics Cards */}
                {stats && Object.keys(stats).length > 0 && (
                    <div className="row g-3 mb-4">
                        {stats.totalTrades !== undefined && (
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card h-100 stats-card stats-card-primary">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Trades</div>
                                                <h2 className="mb-0 fw-bold text-primary">{stats.totalTrades || 0}</h2>
                                            </div>
                                            <div className="stats-icon stats-icon-primary">
                                                <i className="icofont-chart-line"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {stats.totalProfit !== undefined && (
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card h-100 stats-card stats-card-success">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Profit</div>
                                                <h2 className="mb-0 fw-bold text-success">{(stats.totalProfit || 0).toFixed(4)}</h2>
                                            </div>
                                            <div className="stats-icon stats-icon-success">
                                                <i className="icofont-arrow-up"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {stats.totalLoss !== undefined && (
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card h-100 stats-card stats-card-danger">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Loss</div>
                                                <h2 className="mb-0 fw-bold text-danger">{(stats.totalLoss || 0).toFixed(4)}</h2>
                                            </div>
                                            <div className="stats-icon stats-icon-danger">
                                                <i className="icofont-arrow-down"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {stats.profitTrades !== undefined && (
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card h-100 stats-card stats-card-info">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="text-muted small text-uppercase mb-2 fw-semibold">Profit Trades</div>
                                                <h2 className="mb-0 fw-bold text-info">{stats.profitTrades || 0}</h2>
                                            </div>
                                            <div className="stats-icon stats-icon-info">
                                                <i className="icofont-check-circled"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Filters */}
                <div className="card mb-4 filter-card">
                    <div className="card-header bg-transparent border-bottom py-3">
                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="icofont-filter me-2 text-primary"></i>
                            Filter & Sort
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Sort By</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="sortBy"
                                    value={filters.sortBy}
                                    onChange={handleFilterChange}
                                >
                                    <option value="createdAt">Created At</option>
                                    <option value="amount">Amount</option>
                                    <option value="profit">Profit</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Sort Order</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="sortOrder"
                                    value={filters.sortOrder}
                                    onChange={handleFilterChange}
                                >
                                    <option value="desc">Descending</option>
                                    <option value="asc">Ascending</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Items Per Page</label>
                                <select
                                    className="form-select form-select-lg"
                                    name="limit"
                                    value={filters.limit}
                                    onChange={handleFilterChange}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TradingView Chart */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card chart-card">
                            <div className="card-header bg-transparent border-bottom py-3">
                                <h6 className="mb-0 fw-bold d-flex align-items-center">
                                    <i className="icofont-chart-histogram me-2 text-primary"></i>
                                    Live Trading Chart
                                </h6>
                            </div>
                            <div className="card-body p-0">
                                <div className="tradingview-chart">
                                    <iframe
                                        src="https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=en#%7B%22allow_symbol_change%22%3Atrue%2C%22calendar%22%3Afalse%2C%22details%22%3Afalse%2C%22autosize%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22hide_side_toolbar%22%3Atrue%2C%22hide_top_toolbar%22%3Afalse%2C%22hide_legend%22%3Afalse%2C%22hide_volume%22%3Afalse%2C%22hotlist%22%3Afalse%2C%22interval%22%3A%22D%22%2C%22save_image%22%3Atrue%2C%22studies%22%3A%5B%5D%2C%22style%22%3A%221%22%2C%22symbol%22%3A%22OANDA%3AEURUSD%22%2C%22theme%22%3A%22dark%22%2C%22timezone%22%3A%22Etc%2FUTC%22%2C%22backgroundColor%22%3A%22%23000000%22%2C%22gridColor%22%3A%22rgba(255%2C%20255%2C%20255%2C%200.1)%22%2C%22watchlist%22%3A%5B%5D%2C%22show_popup_button%22%3Afalse%2C%22popup_width%22%3A%221000%22%2C%22popup_height%22%3A%22650%22%2C%22compareSymbols%22%3A%5B%5D%2C%22support_host%22%3A%22https%3A%2F%2Fwww.tradingview.com%22%2C%22utm_source%22%3A%22www.tradingview.com%22%2C%22utm_medium%22%3A%22widget_new%22%2C%22utm_campaign%22%3A%22advanced-chart%22%2C%22page-uri%22%3A%22www.tradingview.com%2Fwidget-wizard%2Fen%2Fdark%2Fadvanced-chart%2F%22%7D"
                                        style={{
                                            width: "100%",
                                            height: "600px",
                                            border: "none",
                                            backgroundColor: "#000000"
                                        }}
                                        allowTransparency="true"
                                        scrolling="no"
                                        allowFullScreen
                                        frameBorder="0"
                                        title="Forex Trading Chart"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trades Table */}
                <div className="card table-card">
                    <div className="card-header py-3 bg-transparent border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="icofont-list me-2 text-primary"></i>
                            Forex Trades
                            {trades.length > 0 && (
                                <span className="badge bg-primary ms-2">{trades.length}</span>
                            )}
                        </h6>
                        <div className="d-flex gap-2">
                            <button
                                className={`btn ${filters.status === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleStatusChange('active')}
                                style={{
                                    borderRadius: '8px',
                                    padding: '0.5rem 1.25rem',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Active
                            </button>
                            <button
                                className={`btn ${filters.status === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleStatusChange('completed')}
                                style={{
                                    borderRadius: '8px',
                                    padding: '0.5rem 1.25rem',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Completed
                            </button>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted mt-3">Loading trades...</p>
                            </div>
                        ) : trades.length > 0 ? (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="fw-semibold">Symbol</th>
                                                <th className="fw-semibold">Quantity</th>
                                                <th className="fw-semibold">Entry Price</th>
                                                <th className="fw-semibold">Current/Exit Price</th>
                                                <th className="fw-semibold">Profit/Loss</th>
                                                <th className="fw-semibold">Status</th>
                                                <th className="fw-semibold">Created At</th>
                                                <th className="fw-semibold">Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trades.map((trade, index) => (
                                                <tr key={trade._id || trade.id || index} className="trade-row">
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="badge bg-primary symbol-badge">
                                                                <i className="icofont-chart-line me-1"></i>
                                                                {trade.symbol || 'N/A'}
                                                            </span>
                                                            {trade.direction && (
                                                                <span className={`badge direction-badge ${
                                                                    trade.direction.toLowerCase() === 'buy' || trade.direction.toLowerCase() === 'long' 
                                                                        ? 'bg-success' 
                                                                        : 'bg-danger'
                                                                }`}>
                                                                    <i className={`icofont-arrow-${trade.direction.toLowerCase() === 'buy' || trade.direction.toLowerCase() === 'long' ? 'up' : 'down'} me-1`}></i>
                                                                    {trade.direction.toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="fw-medium">{(trade.quantity || 0).toFixed(4)}</td>
                                                    <td className="fw-medium">{(trade.entryPrice || 0).toFixed(2)}</td>
                                                    <td className="fw-medium">
                                                        {trade.status === 'active' && liveRates.length > 0 ? (
                                                            <>
                                                                {getCurrentPrice(trade)}
                                                                <i className="icofont-check-circled text-success ms-1" style={{ fontSize: '0.75rem' }} title="Live Price"></i>
                                                            </>
                                                        ) : (
                                                            formatWithAllDecimals(trade.currentPrice || trade.exitPrice || 0)
                                                        )}
                                                    </td>
                                                    <td>
                                                        {trade.status === 'active' && liveRates.length > 0 ? (
                                                            <span className={`fw-bold ${calculateLivePL(trade) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                {calculateLivePL(trade) >= 0 ? '+' : ''}{calculateLivePL(trade).toFixed(4)}
                                                                <i className="icofont-check-circled text-success ms-1" style={{ fontSize: '0.75rem' }} title="Live P/L"></i>
                                                            </span>
                                                        ) : (
                                                            <span className={`fw-bold ${trade.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                {trade.profit >= 0 ? '+' : ''}{(trade.profit || trade.profitLoss || 0).toFixed(4)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-lg ${
                                                            trade.status === 'active' ? 'bg-success' :
                                                            trade.status === 'completed' ? 'bg-secondary' :
                                                            'bg-warning'
                                                        }`}>
                                                            {trade.status ? trade.status.charAt(0).toUpperCase() + trade.status.slice(1) : 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">
                                                        {trade.openManualDate 
                                                            ? `${trade.openManualDate} ${trade.openManualTime || ''}`.trim()
                                                            : (trade.openDate 
                                                                ? new Date(trade.openDate).toLocaleDateString('en-US', { 
                                                                    year: 'numeric', 
                                                                    month: 'short', 
                                                                    day: 'numeric' 
                                                                })
                                                                : 'N/A'
                                                            )
                                                        }
                                                    </td>
                                                    <td>
                                                        <Button 
                                                            variant="light" 
                                                            size="sm"
                                                            style={{ 
                                                                backgroundColor: '#132720',
                                                                color: '#fff',
                                                                border: 'none'
                                                            }}
                                                            onClick={() => {
                                                                setSelectedTrade(trade);
                                                                setShowDetailsModal(true);
                                                            }}
                                                        >
                                                            Details
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination && pagination.pages > 1 && (
                                    <div className="card-footer bg-transparent border-top py-3">
                                        <nav aria-label="Page navigation">
                                            <ul className="pagination justify-content-center mb-0">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                        disabled={pagination.page === 1}
                                                    >
                                                        <i className="icofont-arrow-left"></i> Previous
                                                    </button>
                                                </li>
                                                {[...Array(pagination.pages)].map((_, i) => {
                                                    const pageNum = i + 1;
                                                    if (
                                                        pageNum === 1 ||
                                                        pageNum === pagination.pages ||
                                                        (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                                                    ) {
                                                        return (
                                                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(pageNum)}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            </li>
                                                        );
                                                    } else if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                                                        return (
                                                            <li key={pageNum} className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                        disabled={pagination.page === pagination.pages}
                                                    >
                                                        Next <i className="icofont-arrow-right"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <div className="empty-state">
                                    <i className="icofont-chart-line fs-1 text-muted mb-3 d-block" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                                    <h5 className="text-muted mb-2">No Forex Trades Found</h5>
                                    <p className="text-muted mb-0">Start trading to see your trades here</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <TradeDetailsModal 
                show={showDetailsModal}
                onHide={() => {
                    setShowDetailsModal(false);
                    setSelectedTrade(null);
                }}
                trade={selectedTrade}
            />

            <style>{`
                /* Statistics Cards */
                .stats-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    transition: all 0.3s ease;
                    overflow: hidden;
                    position: relative;
                }
                .stats-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, var(--card-color, #007bff), var(--card-color-light, #66b3ff));
                }
                .stats-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
                }
                .stats-card-primary::before {
                    background: linear-gradient(90deg, #007bff, #66b3ff);
                }
                .stats-card-success::before {
                    background: linear-gradient(90deg, #28a745, #71dd8a);
                }
                .stats-card-danger::before {
                    background: linear-gradient(90deg, #dc3545, #f5a6b0);
                }
                .stats-card-info::before {
                    background: linear-gradient(90deg, #17a2b8, #6ed4e8);
                }
                .stats-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                    opacity: 0.15;
                }
                .stats-icon-primary {
                    background: rgba(0, 123, 255, 0.1);
                    color: #007bff;
                }
                .stats-icon-success {
                    background: rgba(40, 167, 69, 0.1);
                    color: #28a745;
                }
                .stats-icon-danger {
                    background: rgba(220, 53, 69, 0.1);
                    color: #dc3545;
                }
                .stats-icon-info {
                    background: rgba(23, 162, 184, 0.1);
                    color: #17a2b8;
                }

                /* Filter Card */
                .filter-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                }
                .filter-card .form-select-lg {
                    border-radius: 8px;
                    border: 1px solid #e0e0e0;
                    transition: all 0.3s ease;
                }
                .filter-card .form-select-lg:focus {
                    border-color: #007bff;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.1);
                }

                /* Chart Card */
                .chart-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                }
                .tradingview-chart {
                    width: 100%;
                    background: #fff;
                    border-radius: 0 0 12px 12px;
                    overflow: hidden;
                }

                /* Table Card */
                .table-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                }
                .table-card .table {
                    margin-bottom: 0;
                }
                .table-card .table thead th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 1rem;
                    font-size: 0.875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #495057;
                }
                .table-card .table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-color: #f0f0f0;
                }
                .table-card .table tbody tr.trade-row {
                    transition: all 0.2s ease;
                }
                .table-card .table tbody tr.trade-row:hover {
                    background-color: #f8f9fa;
                    transform: scale(1.01);
                }
                .badge-lg {
                    padding: 0.5rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 6px;
                }
                .symbol-badge {
                    padding: 0.5rem 0.875rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    white-space: nowrap;
                }
                .direction-badge {
                    padding: 0.5rem 0.875rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    white-space: nowrap;
                }

                /* Pagination */
                .pagination .page-link {
                    border-radius: 8px;
                    margin: 0 2px;
                    border: 1px solid #e0e0e0;
                    color: #495057;
                    transition: all 0.3s ease;
                }
                .pagination .page-link:hover {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: #fff;
                    transform: translateY(-2px);
                }
                .pagination .page-item.active .page-link {
                    background-color: #007bff;
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* Empty State */
                .empty-state {
                    padding: 3rem 1rem;
                }

                @media (max-width: 768px) {
                    .tradingview-chart iframe {
                        height: 400px !important;
                    }
                    .stats-card {
                        margin-bottom: 1rem;
                    }
                    .table-card .table {
                        font-size: 0.875rem;
                    }
                    .table-card .table thead th,
                    .table-card .table tbody td {
                        padding: 0.75rem 0.5rem;
                    }
                }
            `}</style>
        </>
    );
}

const mapStateToProps = (state) => ({
    forexTrades: state.data?.trades?.forex,
    loading: state.data?.loading?.forexTrades || false,
});

export default connect(mapStateToProps, {
    fetchForexTrades
})(ForexPage);
